import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Plus, Edit3, Trash2 } from "lucide-react";

import { api, absUrl } from "../../lib/api";
import { PageHeader, AdminBody, Card, Button, Table, Modal, Toolbar } from "./Bits";
import MediaPicker from "./MediaPicker";

/**
 * Generic CRUD admin for simple resources.
 * fields: [{name, label, type: 'text'|'textarea'|'number'|'select'|'media'|'checkbox'|'list'|'tags', options?, placeholder?}]
 */
export default function CRUDAdmin({
  title, subtitle, endpoint, fields, columns, defaults = {}, testIdPrefix = "row",
  emptyText = "No items yet — add the first one.",
}) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);

  const load = () => api.get(endpoint).then(r => setRows(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setDraft({ ...defaults }); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setDraft({ ...r }); setOpen(true); };

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...draft };
      if (editing) await api.put(`${endpoint}/${editing.id}`, payload);
      else await api.post(endpoint, payload);
      toast.success("Saved");
      setOpen(false); load();
    } catch (e) { toast.error("Save failed"); } finally { setBusy(false); }
  };

  const remove = async (r) => {
    if (!window.confirm("Delete this item?")) return;
    await api.delete(`${endpoint}/${r.id}`); toast.success("Deleted"); load();
  };

  const setField = (k, v) => setDraft(prev => ({ ...prev, [k]: v }));

  return (
    <>
      <PageHeader title={title} subtitle={subtitle}
        action={<Button onClick={openNew} data-testid={`${testIdPrefix}-add`}><Plus size={14} /> Add New</Button>} />
      <AdminBody>
        <Card>
          <Table
            columns={columns}
            rows={rows}
            empty={emptyText}
            testIdPrefix={testIdPrefix}
            actions={(r) => (
              <div className="flex gap-2 justify-end">
                <button onClick={() => openEdit(r)} className="text-navy-900 hover:text-gold p-2" data-testid={`${testIdPrefix}-edit-${r.id}`}><Edit3 size={14} /></button>
                <button onClick={() => remove(r)} className="text-maroon hover:opacity-70 p-2" data-testid={`${testIdPrefix}-del-${r.id}`}><Trash2 size={14} /></button>
              </div>
            )}
          />
        </Card>

        <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit" : "New"} maxWidth="max-w-3xl">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {fields.map(f => (
              <div key={f.name} className={f.full ? "sm:col-span-2" : ""}>
                <label className="label">{f.label}</label>
                {f.type === "textarea" && <textarea className="input min-h-[90px]" value={draft[f.name] || ""} placeholder={f.placeholder} onChange={e => setField(f.name, e.target.value)} data-testid={`f-${f.name}`} />}
                {(!f.type || f.type === "text") && <input className="input" value={draft[f.name] || ""} placeholder={f.placeholder} onChange={e => setField(f.name, e.target.value)} data-testid={`f-${f.name}`} />}
                {f.type === "number" && <input type="number" className="input" value={draft[f.name] ?? ""} placeholder={f.placeholder} onChange={e => setField(f.name, e.target.value === "" ? null : parseFloat(e.target.value))} data-testid={`f-${f.name}`} />}
                {f.type === "select" && (
                  <select className="input" value={draft[f.name] || ""} onChange={e => setField(f.name, e.target.value)} data-testid={`f-${f.name}`}>
                    <option value="">—</option>
                    {f.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                )}
                {f.type === "checkbox" && (
                  <label className="flex items-center gap-2 mt-2"><input type="checkbox" checked={!!draft[f.name]} onChange={e => setField(f.name, e.target.checked)} className="accent-[#D4AF37] w-4 h-4" data-testid={`f-${f.name}`} /><span className="text-sm">{f.checkboxLabel || "Enabled"}</span></label>
                )}
                {f.type === "media" && <MediaPicker value={draft[f.name]} onChange={v => setField(f.name, v)} />}
                {f.type === "list" && (
                  <ListField value={draft[f.name] || []} onChange={v => setField(f.name, v)} placeholder={f.placeholder} />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="modal-cancel">Cancel</Button>
            <Button onClick={save} disabled={busy} data-testid="modal-save">{busy ? "Saving…" : "Save"}</Button>
          </div>
        </Modal>
      </AdminBody>
    </>
  );
}

function ListField({ value, onChange, placeholder }) {
  const [text, setText] = useState("");
  const add = () => { if (text.trim()) { onChange([...value, text.trim()]); setText(""); } };
  return (
    <div>
      <div className="flex gap-2">
        <input className="input" value={text} onChange={e => setText(e.target.value)} placeholder={placeholder || "Add item"} onKeyDown={e => e.key === "Enter" && (e.preventDefault(), add())} />
        <button type="button" onClick={add} className="btn-gold !py-2 !px-4 !text-[10px]">Add</button>
      </div>
      <ul className="mt-3 space-y-2">
        {value.map((v, i) => (
          <li key={i} className="flex items-center justify-between gap-2 bg-ivory-200 px-3 py-2 text-sm">
            <span>{v}</span>
            <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} className="text-maroon hover:opacity-70"><Trash2 size={12} /></button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export { absUrl };
